import { Fragment, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';
import { ERROR_CATEGORIES, categoryMeta } from '../../utils/errorCategories';

const api = axios.create({ baseURL: '/api/platform', withCredentials: true });

const inputStyle = {
    padding: '0.6rem 0.75rem', borderRadius: '0.5rem',
    border: '1px solid var(--input-border)', background: 'var(--input-bg)',
    color: 'var(--text-main)', fontSize: '0.85rem',
};
const th = { padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 };
const td = { padding: '0.875rem 1rem', fontSize: '0.85rem', verticalAlign: 'top' };

// Cross-tenant error log for the platform admin, with the technical detail
// (error code, request, stack trace) that tenant admins don't see on their
// own System Logs screen.
export default function ErrorLogsPanel({ tenants = [] }) {
    const [filters, setFilters] = useState({ tenantId: '', category: '', search: '' });
    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState(null);
    const limit = 50;

    const { data, isLoading } = useQuery({
        queryKey: ['platform-error-logs', filters, page],
        queryFn: () => {
            const params = new URLSearchParams({ page, limit });
            if (filters.tenantId) params.append('tenantId', filters.tenantId);
            if (filters.category) params.append('category', filters.category);
            if (filters.search) params.append('search', filters.search);
            return api.get(`error-logs?${params.toString()}`).then(r => r.data);
        },
        placeholderData: keepPreviousData,
    });

    const logs = data?.logs || [];
    const total = data?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / limit));

    const updateFilter = (key, value) => {
        setPage(1);
        setFilters(f => ({ ...f, [key]: value }));
    };

    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                <select value={filters.tenantId} onChange={e => updateFilter('tenantId', e.target.value)} style={inputStyle}>
                    <option value="">All tenants</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select value={filters.category} onChange={e => updateFilter('category', e.target.value)} style={inputStyle}>
                    <option value="">All categories</option>
                    {ERROR_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <input
                    placeholder="Search message, cause, code or path…"
                    value={filters.search}
                    onChange={e => updateFilter('search', e.target.value)}
                    style={{ ...inputStyle, gridColumn: 'span 2' }}
                />
            </div>

            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                {isLoading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                                <tr>
                                    <th style={th}>Timestamp</th>
                                    <th style={th}>Tenant</th>
                                    <th style={th}>Category</th>
                                    <th style={th}>What failed</th>
                                    <th style={th}>Likely cause</th>
                                    <th style={th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                            No errors logged
                                        </td>
                                    </tr>
                                ) : logs.map(log => {
                                    const meta = categoryMeta(log.category || 'UNCLASSIFIED');
                                    const isOpen = expanded === log.id;
                                    return (
                                        <Fragment key={log.id}>
                                            <tr style={{ borderBottom: isOpen ? 'none' : '1px solid var(--border)' }}>
                                                <td style={{ ...td, fontSize: '0.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                                    {new Date(log.createdAt).toLocaleString()}
                                                </td>
                                                <td style={td}>{log.tenant?.name || 'Platform'}</td>
                                                <td style={td}>
                                                    <span style={{
                                                        padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.72rem',
                                                        fontWeight: 600, color: 'white', background: meta.color, whiteSpace: 'nowrap',
                                                    }}>
                                                        {meta.label}
                                                    </span>
                                                </td>
                                                <td style={td}>{log.message}</td>
                                                <td style={{ ...td, color: 'var(--text-muted)', fontSize: '0.8rem', maxWidth: '22rem' }}>{log.cause || '—'}</td>
                                                <td style={td}>
                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={() => setExpanded(isOpen ? null : log.id)}
                                                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                                    >
                                                        {isOpen ? 'Hide' : 'Details'}
                                                    </button>
                                                </td>
                                            </tr>
                                            {isOpen && (
                                                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                                                    <td colSpan="6" style={{ padding: '1rem' }}>
                                                        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.35rem 1rem', fontSize: '0.8rem', margin: 0 }}>
                                                            <dt style={{ color: 'var(--text-muted)' }}>Error code</dt><dd style={{ margin: 0, fontFamily: 'monospace' }}>{log.errorCode || '—'}</dd>
                                                            <dt style={{ color: 'var(--text-muted)' }}>Request</dt><dd style={{ margin: 0, fontFamily: 'monospace' }}>{log.method ? `${log.method} ${log.requestPath}` : '—'}</dd>
                                                            <dt style={{ color: 'var(--text-muted)' }}>Status code</dt><dd style={{ margin: 0 }}>{log.statusCode ?? '—'}</dd>
                                                            <dt style={{ color: 'var(--text-muted)' }}>Source</dt><dd style={{ margin: 0 }}>{log.source}</dd>
                                                            <dt style={{ color: 'var(--text-muted)' }}>User</dt><dd style={{ margin: 0 }}>{log.user ? `${log.user.name} (${log.user.email})` : '—'}</dd>
                                                            {log.metadata && (<><dt style={{ color: 'var(--text-muted)' }}>Metadata</dt><dd style={{ margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>{log.metadata}</dd></>)}
                                                        </dl>
                                                        {log.stack && (
                                                            <pre style={{
                                                                marginTop: '0.75rem', padding: '0.75rem', background: 'var(--surface)',
                                                                border: '1px solid var(--border)', borderRadius: '0.5rem',
                                                                fontSize: '0.72rem', overflowX: 'auto', whiteSpace: 'pre', maxHeight: '18rem',
                                                            }}>
                                                                {log.stack}
                                                            </pre>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {total > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <span>Page {page} of {totalPages} ({total} total)</span>
                    <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                        Prev
                    </button>
                    <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}
